<template>
  <Dialog
    v-model:visible="localVisible"
    modal
    maximizable
    :header="name"
    :style="{ width: '50rem' }"
    :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
  >
    <div class="">
      <div
        v-if="Helper.isImage({ type: type }) && !currentImageSrc && !imageError"
        class="flex justify-center items-center p-12"
      >
        <ProgressSpinner
          style="width: 48px; height: 48px"
          strokeWidth="3"
          aria-label="Loading..."
        />
      </div>
      <img
        v-else-if="Helper.isImage({ type: type }) && currentImageSrc && !imageError"
        :src="currentImageSrc"
        :alt="name"
        class="w-full h-full object-cover border rounded"
        @error="handleImageError"
        @load="handleImageLoad"
      />
      <div
        v-else-if="Helper.isImage({ type: type }) && imageError"
        class="flex items-center justify-center p-8 text-center"
      >
        <div class="text-red-500">
          <i class="pi pi-exclamation-triangle text-2xl mb-2"></i>
          <p class="text-lg font-semibold">Image could not be loaded</p>
          <p class="text-sm text-gray-600 mt-1">
            Both remote and local sources failed to load
          </p>
        </div>
      </div>
      <VuePdfEmbed
        v-else-if="Helper.isPdf({ type: type })"
        annotation-layer
        text-layer
        :source="url"
      />
      <div
        v-else-if="Helper.isHTML({ type: type })"
        v-html="url"
      ></div>
      <div v-else>{{ `No Preview Available` }}</div>
    </div>
  </Dialog>
</template>

<script>
  import { defineComponent, ref, watch, onBeforeUnmount } from "vue";
  import Helper from "../utils/helper";

  export default defineComponent({
    name: "SnapShotViewDialog",
    props: {
      visible: {
        type: Boolean,
        required: true
      },
      url: {
        type: String,
        required: true
      },
      local_uri: {
        type: String,
        default: ""
      },
      name: {
        type: String,
        default: "Preview"
      },
      type: {
        type: String,
        default: "test"
      }
    },
    emits: ["update:visible"],
    setup(props, { emit }) {
      const localVisible = ref(props.visible);
      const currentImageSrc = ref("");
      const imageError = ref(false);
      const hasTriedUrl = ref(false);
      /** Blob URL from SVG conversion; revoke on close */
      let lastSvgBlobUrl = null;

      const isSvgType = (props.type || "").toLowerCase() === "image/svg+xml";

      // Watch the visible prop and update localVisible accordingly
      watch(
        () => props.visible,
        (newVal) => {
          localVisible.value = newVal;
          if (newVal) {
            imageError.value = false;
            hasTriedUrl.value = false;
            const src = props.local_uri || props.url;
            if (isSvgType && src) {
              currentImageSrc.value = "";
              Helper.convertSvgUrlToRasterUrl(src)
                .then((blobUrl) => {
                  if (lastSvgBlobUrl) URL.revokeObjectURL(lastSvgBlobUrl);
                  lastSvgBlobUrl = blobUrl;
                  currentImageSrc.value = blobUrl;
                })
                .catch(() => {
                  imageError.value = true;
                });
            } else {
              currentImageSrc.value = src || "";
            }
          } else if (lastSvgBlobUrl) {
            URL.revokeObjectURL(lastSvgBlobUrl);
            lastSvgBlobUrl = null;
          }
        },
        { immediate: false }
      );

      // Keep in sync when props change while dialog is open
      watch(
        () => [props.url, props.local_uri],
        () => {
          if (localVisible.value && props.visible) {
            if (props.local_uri) {
              currentImageSrc.value = props.local_uri;
            } else {
              currentImageSrc.value = props.url;
            }
          }
        }
      );

      // Watch localVisible and emit changes to the parent
      watch(
        () => localVisible.value,
        (newVal) => {
          emit("update:visible", newVal);
        }
      );

      const handleImageError = () => {
        if (!hasTriedUrl.value && props.url) {
          // Try URL as fallback
          currentImageSrc.value = props.url;
          hasTriedUrl.value = true;
        } else {
          // Both local_uri and URL failed
          imageError.value = true;
        }
      };

      const handleImageLoad = () => {
        // Image loaded successfully, reset error state
        imageError.value = false;
      };

      const closeDialog = () => {
        localVisible.value = false;
      };

      onBeforeUnmount(() => {
        if (lastSvgBlobUrl) URL.revokeObjectURL(lastSvgBlobUrl);
      });

      return {
        localVisible,
        currentImageSrc,
        imageError,
        handleImageError,
        handleImageLoad,
        closeDialog,
        Helper
      };
    }
  });
</script>
